/**
 * Blocos / torres na tela (0.1.90, Doc. Técnica §69).
 *
 * - `useBlocos`: rótulo ("Bloco"/"Torre") e a lista de blocos do condomínio.
 * - `PublicoSeletor`: "Para quem" de um aviso — o condomínio todo ou blocos escolhidos, e
 *   "Todos / Só proprietários / Só quem mora". Sem blocos cadastrados, mostra só a
 *   segunda parte.
 * - `SeletorBloco`: um bloco só (manutenção do elevador do Bloco B) ou o condomínio todo.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface Bloco { id: number; nome: string; sigla: string; ordem: number }
export interface BlocosResp {
  rotulo: string; blocos: Bloco[];
  unidades: { unidade: string; bloco_id: number | null; bloco_sigla: string | null; origem: string | null }[];
  pode_gerir: boolean;
}
export interface Publico { blocos: number[]; relacao: "todos" | "proprietarios" | "moradores" }
export const PUBLICO_TODOS: Publico = { blocos: [], relacao: "todos" };

export function useBlocos(condominioId: number) {
  return useQuery<BlocosResp>({
    queryKey: ["blocos", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/blocos`),
    staleTime: 60_000,
  });
}

/** O que mandar ao servidor: null = todo o condomínio (o comportamento de sempre). */
export function publicoParaEnvio(p: Publico): Publico | null {
  return !p.blocos.length && p.relacao === "todos" ? null : p;
}

/** "do bloco escolhido" / "da torre escolhida" / "dos blocos…" / "das torres…". */
export function trechoBlocos(rotulo: string, n: number): string {
  const torre = rotulo === "Torre";
  if (n === 1) return torre ? "da torre escolhida" : "do bloco escolhido";
  return torre ? "das torres escolhidas" : "dos blocos escolhidos";
}

const chip = (ativo: boolean) =>
  `text-[12px] px-2.5 py-1 rounded-full border ${ativo ? "bg-brand-700 text-white border-brand-700" : "bg-white text-slate-700 border-slate-300"}`;

export function PublicoSeletor({ condominioId, valor, onChange }: {
  condominioId: number; valor: Publico; onChange: (p: Publico) => void;
}) {
  const { data } = useBlocos(condominioId);
  const blocos = data?.blocos ?? [];
  const rot = data?.rotulo ?? "Bloco";
  const alternar = (id: number) => onChange({
    ...valor, blocos: valor.blocos.includes(id) ? valor.blocos.filter((b) => b !== id) : [...valor.blocos, id],
  });
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-2">
      <div className="text-[12px] font-medium text-slate-600">Para quem</div>
      {blocos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => onChange({ ...valor, blocos: [] })} className={chip(!valor.blocos.length)}>
            Todo o condomínio
          </button>
          {blocos.map((b) => (
            <button key={b.id} type="button" onClick={() => alternar(b.id)} className={chip(valor.blocos.includes(b.id))}>
              {b.nome}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {([["todos", "Todos"], ["proprietarios", "Só proprietários"], ["moradores", "Só quem mora"]] as const).map(([v, r]) => (
          <button key={v} type="button" onClick={() => onChange({ ...valor, relacao: v })} className={chip(valor.relacao === v)}>
            {r}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">
        {valor.blocos.length ? `Só quem é ${trechoBlocos(rot, valor.blocos.length)} recebe e vê no Portal.`
          : "Todo o condomínio recebe."}
        {valor.relacao === "proprietarios" && " Só os proprietários (inquilinos não)."}
        {valor.relacao === "moradores" && " Só quem mora (o dono de apartamento alugado não)."}
      </p>
    </div>
  );
}

export function SeletorBloco({ condominioId, valor, onChange, className }: {
  condominioId: number; valor: number | null; onChange: (id: number | null) => void; className?: string;
}) {
  const { data } = useBlocos(condominioId);
  const blocos = data?.blocos ?? [];
  if (!blocos.length) return null;
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className={className ?? "rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white"}
      title={(data?.rotulo ?? "Bloco") === "Torre"
        ? "Manutenção de uma torre só: avisa e aparece só para quem é dela"
        : "Manutenção de um bloco só: avisa e aparece só para quem é dele"}>
      <option value="">Condomínio todo</option>
      {blocos.map((b) => <option key={b.id} value={b.id}>{b.nome}</option>)}
    </select>
  );
}
